import { useQuery } from '@apollo/client';
import { NotificationChannel } from '@provision/entity-types';
import { useMemo } from 'react';
import { useGroupRules, UserGroupRules } from 'src/graphql/entities/group-rules';
import { NotificationChannelType } from 'src/graphql/entities/notification';
import { GET_NOTIFICATION_CHANNEL_QUERY } from 'src/graphql/entities/notification-preference/notifications.operations';
import { ConditionInput, QueryVariables, Records } from 'src/graphql/types';

export type NotificationChannelResponse = Pick<NotificationChannel, 'id' | 'name'>;

type GetNotificationChannelsQuery = {
  getNotificationChannel: Records<NotificationChannelResponse>;
};

export const useGetNotificationChannels = (options: { isActive?: boolean } = {}) => {
  const { checkValidity } = useGroupRules();
  const hasAccessToInAppNotifications = checkValidity([UserGroupRules.AccessToInbox]);
  const filters = useMemo((): ConditionInput[] => {
    const conditions = [
      {
        attribute: 'isActive',
        operator: '=',
        value: String(options.isActive ?? 'true'),
      },
      {
        // Slack channel is only supported for Assignee Group Mention Feed events
        // which are not user-configurable at the moment.
        attribute: 'id',
        operator: '!=',
        value: 'slack',
      },
    ];
    if (!hasAccessToInAppNotifications) {
      conditions.push({
        attribute: 'notificationChannelId',
        operator: '!=',
        value: NotificationChannelType.InApp,
      });
    }
    return conditions;
  }, [hasAccessToInAppNotifications, options.isActive]);
  const { data } = useQuery<GetNotificationChannelsQuery, QueryVariables>(
    GET_NOTIFICATION_CHANNEL_QUERY,
    {
      variables: {
        params: {
          filters,
        },
      },
    },
  );
  const notificationChannels = useMemo(
    () => data?.getNotificationChannel.records || [],
    [data?.getNotificationChannel.records],
  );
  return {
    notificationChannels,
  };
};
